// src/layouts/RootLayout.tsx
// Protected layout for all authenticated admin pages.
// Renders the Sidebar + TopBar shell and the page content via <Outlet />.
// The authLoader guarantees only authenticated users reach this layout.

import { Outlet } from 'react-router-dom'
import { useEffect } from 'react'
import { Sidebar } from '@/components/Sidebar'
import { TopBar } from '@/components/TopBar'
import { useAuthStore } from '@/stores/authStore'
import LogoWhite from '@/assets/logo_white.png'

export function RootLayout() {
  const { loading, initialize } = useAuthStore()

  // Ensure auth is initialized (handles direct navigation / page refresh)
  useEffect(() => {
    initialize()
  }, [initialize])

  // Full-screen loading overlay while auth state resolves
  if (loading) {
    return (
      <div
        className="fixed inset-0 flex flex-col items-center justify-center z-50"
        style={{ background: 'linear-gradient(145deg, #1E3A5F 0%, #0D2240 100%)' }}
      >
        {/* Church logo */}
        <div
          className="w-32 h-32 flex items-center justify-center mb-6"
        >
          <img src={LogoWhite} alt="SGS Logo" className="w-24 h-24 object-contain" />
        </div>

        {/* App name */}
        <h1 className="text-white font-bold text-xl mb-1">SGS Connect</h1>
        <p className="text-sm mb-8" style={{ color: 'rgba(212,168,67,0.8)' }}>
          Admin Dashboard
        </p>

        {/* Loading spinner */}
        <div className="flex items-center gap-3">
          <div
            className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: 'rgba(212,168,67,0.4)', borderTopColor: '#D4A843' }}
          />
          <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Loading...
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* ── Sidebar ─────────────────────────────────────── */}
      <Sidebar />

      {/* ── Main content area ────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top bar */}
        <TopBar />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-background">
          <div className="p-6 max-w-screen-2xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
